import { useCallback } from "react";
import { Area } from "react-easy-crop";

import { useUpdateAvatar } from "@/services/profile/upload-user-image";
import { showCustomNotification } from "@/utils/notifications";

// Function to create an image element from a given URL
export function getCreateAvatarImage() {
  return (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });
}

// Function to create a cropped image blob from the original image and crop area
export function createCroppedImage(createImage: (url: string) => Promise<HTMLImageElement>) {
  return async (imageSrc: string, pixelCrop: Area): Promise<Blob | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    });
  };
}

// Function to handle image upload and set the image source
export function handleImageUpload(setImageSrc: (src: string) => void, open: () => void) {
  return async (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result as string);
        open();
      });
      reader.readAsDataURL(file);
    }
  };
}

// Function to create a cropped area handler
export function createCroppedAreaHandler(setCroppedAreaPixels: (area: Area | null) => void) {
  return useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
}

// Function to reset avatar settings
export function resetAvatarSettings(
  close: () => void,
  setImageSrc: (src: string | null) => void,
  setCrop: (crop: { x: number; y: number }) => void,
  setZoom: (zoom: number) => void
) {
  return () => {
    close();
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };
}

// Function to handle the submission of the new user image
export function confirmAvatarUpdate(
  imageSrc: string | null,
  croppedAreaPixels: Area | null,
  fileName: string,
  setAvatarUrl: (url: { url: string; name: string } | null) => void,
  updateAvatarMutation: ReturnType<typeof useUpdateAvatar>,
  t: (key: string) => string,
  close: () => void,
  setImageSrc: (src: string | null) => void,
  setCrop: (crop: { x: number; y: number }) => void,
  setZoom: (zoom: number) => void,
  getCroppedImg: (imageSrc: string, pixelCrop: Area) => Promise<Blob | null>
) {
  return async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedBlob) {
          const croppedUrl = URL.createObjectURL(croppedBlob);

          setAvatarUrl({
            name: fileName,
            url: croppedUrl,
          });

          await updateAvatarMutation.mutateAsync({
            avatarBlob: croppedBlob,
            fileName,
          });
        }
      } catch (error) {
        showCustomNotification({
          title: t("components.CreateCar.error.title"),
          message: t("components.CreateCar.error.message"),
          autoClose: 6000,
        });
      }
    }
    close();
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };
}
